import React, { useState, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import { format, parse } from 'date-fns';
import { doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject, getDownloadURL } from 'firebase/storage';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { AppContext } from '../AppContext';
import { db, storage } from '../firebaseConfig';

import { trackFeature } from '../utils/analytics';

export default function InvoiceTable({ invoices = [], setError, setSuccess }) {
  const { user, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;

  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';

  // Local state for search, pagination and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('orderNumber'); // default sort column
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  // Confirmation dialog state for deletion
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // --- Helper to format the invoiceDate field ---
  const formatCompletedDate = (dateValue) => {
    if (!dateValue) return '';

    try {
      let dateObj;
      if (dateValue.toDate) {
        // When it's a Firestore Timestamp
        dateObj = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        // Instead of parseISO, use parse with the default date format.
        dateObj = parse(dateValue, defaultDateFormat, new Date());
      } else {
        dateObj = new Date(dateValue);
      }
      return format(dateObj, defaultDateFormat);
    } catch (err) {
      console.error('Error parsing invoiceDate:', dateValue, err);
      return dateValue;
    }
  };

  // Filter by customer name (or order number if needed)
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) =>
      inv.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [invoices, searchTerm]);

  // Sorted invoices
  const sortedInvoices = useMemo(() => {
    const sorted = [...filteredInvoices].sort((a, b) => {
      let aValue, bValue;
      switch (sortColumn) {
        case 'orderNumber':
          aValue = parseInt(a.orderNumber, 10) || 0;
          bValue = parseInt(b.orderNumber, 10) || 0;
          break;
        case 'customerName': {
          // Using a separate variable name inside a block to avoid conflict.
          const aVal = a.customerName || '';
          const bVal = b.customerName || '';
          const comparison = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
          return sortDirection === 'asc' ? comparison : -comparison;
        }
        case 'invoiceDate':
          aValue = new Date(a.invoiceDate?.toDate ? a.invoiceDate.toDate() : a.invoiceDate);
          bValue = new Date(b.invoiceDate?.toDate ? b.invoiceDate.toDate() : b.invoiceDate);
          break;
        case 'total':
          // Extract numeric value from a formatted total string (e.g., "£45.50")
          aValue = parseFloat(a.total.replace(/[^0-9.]/g, '')) || 0;
          bValue = parseFloat(b.total.replace(/[^0-9.]/g, '')) || 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredInvoices, sortColumn, sortDirection]);


  // Paginate the sorted results
  const paginatedInvoices = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedInvoices.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedInvoices, page, rowsPerPage]);

  // Handlers for pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handler for sorting changes
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // MUI styling for table cells with sort label overrides
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
    '& .MuiTableSortLabel-root': {
      color: `${theme.palette.common.white} !important`,
      '&.Mui-active': {
        color: `${theme.palette.common.white} !important`,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
      },
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.75) !important',
      },
    },
  }));

  // Download PDF from Storage
  const handleDownloadClick = async (invoice) => {
    if (!invoice?.storagePath || !user) return;
    try {
      const storageRef = ref(storage, invoice.storagePath);
      const downloadUrl = await getDownloadURL(storageRef);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      setError?.('Error fetching download URL.');
    }
  };

  // Delete invoice logic
  const handleDeleteClick = (invoice) => {
    setInvoiceToDelete(invoice);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!invoiceToDelete || !user) return;
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
    try {
      if (invoiceToDelete.storagePath) {
        const storageRef = ref(storage, invoiceToDelete.storagePath);          
        await deleteObject(storageRef);
      }
      await deleteDoc(doc(db, 'farms', activeFarmId, 'invoices', invoiceToDelete.id));
      trackFeature('invoice_deleted', {
        invoiceId:   invoiceToDelete.id,
        orderNumber: invoiceToDelete.orderNumber,
        customer:    invoiceToDelete.customerName,
      });
      setSuccess?.('Invoice record and PDF successfully deleted.');
    } catch (error) {
      setError?.('Error deleting invoice and PDF.');
    }
    setConfirmOpen(false);
    setInvoiceToDelete(null);
  };
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setInvoiceToDelete(null);
  };

  return (
    <Box>
      <Typography variant="h6">Past Orders</Typography>

      {/* Search Field */}
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <TextField
          label="Search by Customer"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'orderNumber'}
                  direction={sortColumn === 'orderNumber' ? sortDirection : 'asc'}
                  onClick={() => handleSort('orderNumber')}
                >
                  Order Number
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'customerName'}
                  direction={sortColumn === 'customerName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('customerName')}
                >
                  Customer
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'total'}
                  direction={sortColumn === 'total' ? sortDirection : 'asc'}
                  onClick={() => handleSort('total')}
                >
                  Total
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={sortColumn === 'invoiceDate'}
                  direction={sortColumn === 'invoiceDate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('invoiceDate')}
                >
                  Date Completed
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>Invoice</StyledTableCell>
              <StyledTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Delete</span>
                  <Tooltip
                    title={
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        This deletes the invoice from the database, non-reversible.
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInvoices.map((inv) => (
              <TableRow 
              key={inv.id} 
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.dark
                    : theme.palette.primary.lighter,
              })}
              >
                <TableCell>{inv.orderNumber}</TableCell>
                <TableCell>{inv.customerName}</TableCell>
                <TableCell>{inv.total}</TableCell>
                <TableCell>{formatCompletedDate(inv.invoiceDate)}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    endIcon={<DownloadIcon />}
                    onClick={() => handleDownloadClick(inv)}
                  >
                    PDF
                  </Button>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={() => handleDeleteClick(inv)}
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'light'
                          ? theme.palette.error.dark
                          : theme.palette.error.main,
                    })}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedInvoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No past orders found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <TablePagination
        component="div"
        count={sortedInvoices.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmOpen}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice and its PDF file?"
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
      />
    </Box>
  );
}

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}



